/**
 * Gated HITL bridge for `interpolate_structure` — fills empty slices of a
 * structure IN PLACE, with the same two choices and the same engine calls
 * as the Interpolation operation in the right-hand operations panel:
 *  - `all-empty`: every empty slice that lies BETWEEN two drawn slices is
 *    interpolated from its nearest drawn neighbours; slices above the top
 *    or below the bottom drawn slice are left empty;
 *  - `current-slice`: the slice shown in the main view. Between two drawn
 *    slices it is interpolated; with a drawn slice on one side only, the
 *    engine copies that nearest drawn slice (as the panel does).
 *
 * Before the card is shown, the slices are inspected with the engine's own
 * rule (a slice is drawn when its SDF exceeds 128), so the card states what
 * will be filled and a no-op request is refused instead of offered. The
 * current slice is captured when the card is built and Apply interpolates
 * THAT slice even if the clinician has scrolled since.
 *
 * Safety mirrors the other in-place ops: ≤1-pending preamble, approved
 * structures refused (re-checked at Apply), a structure-set switch or an
 * edit of the structure between card and Apply is refused. There is no
 * geometry preview (the engine writes directly), so the card states the
 * effect instead. Pure/React-free; the engine is injected.
 */
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    notModifiableReason,
    reasonOf,
    resolveFailureToPlan,
    resolveOneStructure,
    toListed,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
    type StructureFacts,
} from '../core';
import type {
    InPlaceEditOutcome,
    InterpolateStructureInput,
} from '../../contract';

export type InterpolationScope = InterpolateStructureInput['scope'];

/** How Apply fills the current slice (the engine's own rule). */
export type CurrentSliceFill = 'between' | 'copy-below' | 'copy-above';

export interface InterpolateDeps extends PendingFlag {
    readonly structures: ReadonlyArray<StructureFacts>;
    canModify(roiNumber: number): boolean;
    currentContextId(): string | undefined;
    /** Changes whenever the structure's contours change. */
    editVersion(roiNumber: number): number | undefined;
    /** Position (mm) of the slice in the main view, as the panel reads it. */
    currentSlicePositionMm(): number | undefined;
    /** Per texture slice (bottom → top): does it hold a contour? */
    drawnSlices(roiNumber: number): ReadonlyArray<boolean> | undefined;
    /** The texture slice the engine interpolates for this position;
     *  undefined when the position is outside the structure's texture. */
    sliceIndexAt(
        roiNumber: number,
        slicePositionMm: number
    ): number | undefined;
    volumeCm3(roiNumber: number): number;
    readonly interpolate: {
        allEmpty(roiNumber: number): void;
        currentSlice(roiNumber: number, slicePositionMm: number): void;
    };
}

export interface InterpolateProposal {
    readonly toolCallId: string;
    readonly target: {
        readonly roiNumber: number;
        readonly name: string;
        readonly currentVolumeCm3: number;
    };
    readonly scope: InterpolationScope;
    /** `all-empty`: how many empty slices will be filled. */
    readonly slicesToFill?: number;
    /** `current-slice`: the slice Apply fills (mm) and how. */
    readonly slicePositionMm?: number;
    readonly currentSliceFill?: CurrentSliceFill;
    apply(): Promise<InPlaceEditOutcome>;
    reject(): InPlaceEditOutcome;
    showPreview(): void;
    hidePreview(): void;
}

export type InterpolatePlan = MutatingPlan<InterpolateProposal>;

/** Empty slices with a drawn slice both below and above. */
export function countFillableSlices(drawn: ReadonlyArray<boolean>): number {
    const first = drawn.indexOf(true);
    const last = drawn.lastIndexOf(true);
    if (first === -1) {
        return 0;
    }
    let n = 0;
    for (let z = first + 1; z < last; z++) {
        if (!drawn[z]) {
            n++;
        }
    }
    return n;
}

/** What the engine does to slice `z`, or why it does nothing. */
export function currentSliceFill(
    drawn: ReadonlyArray<boolean>,
    z: number
): { readonly fill: CurrentSliceFill } | { readonly refused: string } {
    if (drawn[z]) {
        return { refused: 'the current slice already has a contour' };
    }
    const below = drawn.slice(0, z).includes(true);
    const above = drawn.slice(z + 1).includes(true);
    if (below && above) {
        return { fill: 'between' };
    }
    if (below) {
        return { fill: 'copy-below' };
    }
    if (above) {
        return { fill: 'copy-above' };
    }
    return {
        refused: 'the structure has no drawn slice to interpolate from',
    };
}

function readInput(input: unknown): InterpolateStructureInput | undefined {
    const o = asRecord(input);
    if (
        typeof o?.roi !== 'string' ||
        !o.roi ||
        (o.scope !== 'all-empty' && o.scope !== 'current-slice')
    ) {
        return undefined;
    }
    return { roi: o.roi, scope: o.scope };
}

export async function planInterpolateStructure(
    call: MutatingToolCall,
    deps: InterpolateDeps
): Promise<InterpolatePlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const parsed = readInput(call.input);
    if (!parsed) {
        return failedPlan(
            call.toolCallId,
            'invalid interpolate_structure input'
        );
    }

    const resolved = resolveOneStructure(
        parsed.roi,
        deps.structures,
        deps.structures.map(toListed),
        'interpolation',
        'interpolate'
    );
    if (resolved.kind !== 'source') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }
    const { roiNumber, name, volumeCm3: volumeBeforeCm3 } = resolved.source;
    if (!deps.canModify(roiNumber)) {
        return failedPlan(call.toolCallId, notModifiableReason(name));
    }
    const drawn = deps.drawnSlices(roiNumber);
    if (!drawn) {
        return failedPlan(
            call.toolCallId,
            `the contours of '${name}' could not be read`
        );
    }

    let slicesToFill: number | undefined;
    let slicePositionMm: number | undefined;
    let fill: CurrentSliceFill | undefined;
    if (parsed.scope === 'all-empty') {
        slicesToFill = countFillableSlices(drawn);
        if (slicesToFill === 0) {
            return failedPlan(
                call.toolCallId,
                `'${name}' has no empty slices between its drawn slices; ` +
                    'nothing to interpolate'
            );
        }
    } else {
        slicePositionMm = deps.currentSlicePositionMm();
        if (
            slicePositionMm === undefined ||
            !Number.isFinite(slicePositionMm)
        ) {
            return failedPlan(
                call.toolCallId,
                'no main view is available to tell which slice is shown'
            );
        }
        const z = deps.sliceIndexAt(roiNumber, slicePositionMm);
        if (z === undefined) {
            return failedPlan(
                call.toolCallId,
                `the current slice is outside the extent of '${name}'; ` +
                    'go to a slice between its drawn slices first'
            );
        }
        const how = currentSliceFill(drawn, z);
        if ('refused' in how) {
            return failedPlan(call.toolCallId, how.refused);
        }
        fill = how.fill;
    }

    // The state this card was built against; Apply refuses if it moved on.
    const plannedContextId = deps.currentContextId();
    const plannedVersion = deps.editVersion(roiNumber);

    const proposal: InterpolateProposal = {
        toolCallId: call.toolCallId,
        target: { roiNumber, name, currentVolumeCm3: volumeBeforeCm3 },
        scope: parsed.scope,
        ...(slicesToFill !== undefined ? { slicesToFill } : {}),
        ...(slicePositionMm !== undefined ? { slicePositionMm } : {}),
        ...(fill !== undefined ? { currentSliceFill: fill } : {}),
        apply: async () => {
            if (deps.currentContextId() !== plannedContextId) {
                return {
                    status: 'failed',
                    reason:
                        'the active structure set changed before this was ' +
                        'applied; re-issue the request against the current set',
                };
            }
            if (!deps.canModify(roiNumber)) {
                return { status: 'failed', reason: notModifiableReason(name) };
            }
            if (deps.editVersion(roiNumber) !== plannedVersion) {
                return {
                    status: 'failed',
                    reason:
                        `'${name}' was edited after this was proposed; ` +
                        're-issue the request',
                };
            }
            try {
                if (parsed.scope === 'all-empty') {
                    deps.interpolate.allEmpty(roiNumber);
                } else {
                    deps.interpolate.currentSlice(
                        roiNumber,
                        slicePositionMm as number
                    );
                }
            } catch (err) {
                return { status: 'failed', reason: reasonOf(err) };
            }
            const volumeAfterCm3 = deps.volumeCm3(roiNumber);
            if (!Number.isFinite(volumeAfterCm3)) {
                return {
                    status: 'failed',
                    reason:
                        'slices were interpolated but the new volume could ' +
                        'not be read; check the structure in the viewer',
                };
            }
            return {
                status: 'applied',
                roiNumber,
                roi: name,
                volumeCm3: volumeAfterCm3,
                volumeDeltaCm3: volumeAfterCm3 - volumeBeforeCm3,
            };
        },
        reject: () => ({ status: 'rejected' }),
        showPreview: () => undefined,
        hidePreview: () => undefined,
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
